import { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import toast from 'react-hot-toast';
import {
  FileText,
  Plus,
  Search,
  Eye,
  Edit,
  Trash2,
  Clock,
  Users,
  BookOpen,
  Target,
  BarChart3,
  Play,
  Pause,
  X,
  Save
} from 'lucide-react';

interface MockTest {
  id: number;
  title: string;
  description: string;
  duration: number;
  totalQuestions: number;
  subjects: string[];
  difficulty: string;
  totalMarks: number;
  negativeMarks: number;
  instructions?: string;
  isActive: boolean;
  createdAt: string;
  totalAttempts?: number;
  completedAttempts?: number;
  avgScore?: number;
}

interface TestFormData {
  title: string;
  description: string;
  duration: number;
  totalQuestions: number;
  subjects: string[];
  difficulty: string;
  totalMarks: number;
  negativeMarks: number;
  instructions: string;
}

export default function AdminTests() {
  const [tests, setTests] = useState<MockTest[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, sets] = useState({
    difficulty: 'all',
    isActive: 'all'
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedTest, setSelectedTest] = useState<MockTest | null>(null);
  const [showTestDetails, setShowTestDetails] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [currentPage, _setCurrentPage] = useState(1);
  const [_totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState({ total: 0, active: 0, totalAttempts: 0, avgScore: 0 });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await adminAPI.getTestStats();
      if (res.data.success) setStats(res.data.data);
    } catch {
      // stats stay at 0
    }
  };

  const [formData, setFormData] = useState<TestFormData>({
    title: '',
    description: '',
    duration: 180,
    totalQuestions: 75,
    subjects: ['mathematics', 'physics', 'chemistry'],
    difficulty: 'medium',
    totalMarks: 300,
    negativeMarks: -1,
    instructions: 'Read all instructions carefully before starting the test.\n\n1. This test contains 75 questions (25 from each subject: Mathematics, Physics, Chemistry)\n2. Each question carries 4 marks for correct answer\n3. There is negative marking of 1 mark for each wrong answer\n4. Total duration is 3 hours (180 minutes)\n5. You can navigate between questions using the question palette\n6. Make sure to submit the test before time expires\n\nAll the best!'
  });

  useEffect(() => {
    fetchTests();
  }, [currentPage, searchTerm, filters]);

  const fetchTests = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: 10,
        search: searchTerm,
        ...(filters.difficulty !== 'all' && { difficulty: filters.difficulty }),
        ...(filters.isActive !== 'all' && { isActive: filters.isActive })
      };

      const response = await adminAPI.getTests(params);
      if (response.data.success) {
        // Handle response structure - data is the array directly, pagination is separate
        const testsData = response.data.data || [];
        const paginationData = response.data.pagination || { pages: 1 };
        
        setTests(testsData);
        setTotalPages(paginationData.pages);
      }
    } catch (error: any) {
      toast.error('Failed to load tests');
      console.error('Fetch tests error:', error);
      // Set empty array on error to prevent undefined errors
      setTests([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTest = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      const response = await adminAPI.createTest(formData);
      if (response.data.success) {
        toast.success('Test created successfully!');
        setShowCreateModal(false);
        resetForm();
        fetchTests();
        fetchStats();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to create test');
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateTest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTest) return;

    setFormLoading(true);
    try {
      const response = await adminAPI.updateTest(selectedTest.id.toString(), formData);
      if (response.data.success) {
        toast.success('Test updated successfully!');
        setShowEditModal(false);
        setSelectedTest(null);
        resetForm();
        fetchTests();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to update test');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteTest = async (testId: number) => {
    if (!confirm('Are you sure you want to delete this test? This action cannot be undone.')) {
      return;
    }

    try {
      await adminAPI.deleteTest(testId.toString());
      toast.success('Test deleted successfully!');
      fetchTests();
      fetchStats();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to delete test');
    }
  };

  const toggleTestStatus = async (testId: number, currentStatus: boolean) => {
    try {
      await adminAPI.updateTest(testId.toString(), { isActive: !currentStatus });
      toast.success(`Test ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
      fetchTests();
    } catch (error: any) {
      toast.error('Failed to update test status');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      duration: 180,
      totalQuestions: 75,
      subjects: ['mathematics', 'physics', 'chemistry'],
      difficulty: 'medium',
      totalMarks: 300,
      negativeMarks: -1,
      instructions: 'Read all instructions carefully before starting the test.\n\n1. This test contains 75 questions (25 from each subject: Mathematics, Physics, Chemistry)\n2. Each question carries 4 marks for correct answer\n3. There is negative marking of 1 mark for each wrong answer\n4. Total duration is 3 hours (180 minutes)\n5. You can navigate between questions using the question palette\n6. Make sure to submit the test before time expires\n\nAll the best!'
    });
  };

  const openEditModal = (test: MockTest) => {
    setSelectedTest(test);
    setFormData({
      title: test.title,
      description: test.description,
      duration: test.duration,
      totalQuestions: test.totalQuestions,
      subjects: test.subjects,
      difficulty: test.difficulty,
      totalMarks: test.totalMarks,
      negativeMarks: test.negativeMarks,
      instructions: test.instructions || ''
    });
    setShowEditModal(true);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty.toLowerCase()) {
      case 'easy': return 'success';
      case 'medium': return 'warning';
      case 'hard': return 'danger';
      default: return 'secondary';
    }
  };

  const getSubjectColor = (subject: string) => {
    switch (subject.toLowerCase()) {
      case 'mathematics': return 'primary';
      case 'physics': return 'success';
      case 'chemistry': return 'warning';
      default: return 'secondary';
    }
  };

  const filteredTests = (tests || []).filter(test => {
    const matchesSearch = test.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         test.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDifficulty = filters.difficulty === 'all' || test.difficulty === filters.difficulty;
    const matchesStatus = filters.isActive === 'all' || 
                         (filters.isActive === 'active' && test.isActive) ||
                         (filters.isActive === 'inactive' && !test.isActive);
    
    return matchesSearch && matchesDifficulty && matchesStatus;
  });

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6 md:mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-secondary-900 mb-2">Test Management</h1>
            <p className="text-sm md:text-base text-secondary-600">Create and manage mock tests for students</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center justify-center px-4 py-2.5 md:py-2 text-sm font-medium text-white bg-primary-600 border border-primary-600 rounded-lg hover:bg-primary-700 transition-colors touch-target"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Test
          </button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 mb-6 md:mb-8">
        <div className="stats-card">
          <div className="flex items-center justify-between mb-3 md:mb-4">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-r from-primary-500 to-primary-600 rounded-xl flex items-center justify-center">
              <FileText className="h-5 w-5 md:h-6 md:w-6 text-white" />
            </div>
          </div>
          <div className="stats-value">{stats.total}</div>
          <div className="stats-label">Total Tests</div>
        </div>

        <div className="stats-card">
          <div className="flex items-center justify-between mb-3 md:mb-4">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-r from-success-500 to-success-600 rounded-xl flex items-center justify-center">
              <Play className="h-5 w-5 md:h-6 md:w-6 text-white" />
            </div>
          </div>
          <div className="stats-value">{stats.active}</div>
          <div className="stats-label">Active Tests</div>
        </div>

        <div className="stats-card">
          <div className="flex items-center justify-between mb-3 md:mb-4">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-r from-warning-500 to-warning-600 rounded-xl flex items-center justify-center">
              <Users className="h-5 w-5 md:h-6 md:w-6 text-white" />
            </div>
          </div>
          <div className="stats-value">{stats.totalAttempts}</div>
          <div className="stats-label">Total Attempts</div>
        </div>

        <div className="stats-card">
          <div className="flex items-center justify-between mb-3 md:mb-4">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-r from-accent-500 to-accent-600 rounded-xl flex items-center justify-center">
              <BarChart3 className="h-5 w-5 md:h-6 md:w-6 text-white" />
            </div>
          </div>
          <div className="stats-value">{stats.avgScore.toFixed(1)}%</div>
          <div className="stats-label">Avg Score</div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="card mb-6">
        <div className="card-content">
          <div className="flex flex-col space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
              <input
                type="text"
                placeholder="Search tests..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2.5 md:py-2 w-full border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <select
                value={filters.difficulty}
                onChange={(e) => sets({ ...filters, difficulty: e.target.value })}
                className="flex-1 min-w-[130px] px-3 py-2.5 md:py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              >
                <option value="all">All Difficulties</option>
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
              </select>
              <select
                value={filters.isActive}
                onChange={(e) => sets({ ...filters, isActive: e.target.value })}
                className="flex-1 min-w-[120px] px-3 py-2.5 md:py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <div className="flex items-center space-x-2 text-sm text-secondary-600 ml-auto">
                <FileText className="h-4 w-4" />
                <span>{filteredTests.length} tests found</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tests Grid */}
      <div className="space-y-6">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary-500 border-t-transparent mx-auto mb-4"></div>
            <p className="text-secondary-600">Loading tests...</p>
          </div>
        ) : filteredTests.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="h-12 w-12 text-secondary-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-secondary-900 mb-2">No Tests Found</h3>
            <p className="text-secondary-600 mb-4">
              {searchTerm || filters.difficulty !== 'all' || filters.isActive !== 'all'
                ? 'No tests match your search criteria.' 
                : 'Get started by creating your first test.'
              }
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-primary-600 rounded-lg hover:bg-primary-700 transition-colors mx-auto"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Test
            </button>
          </div>
        ) : (
          filteredTests.map((test) => (
              <div key={test.id} className="card-interactive group">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between p-4 md:p-6 gap-4">
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <h3 className="text-lg md:text-xl font-semibold text-secondary-900 group-hover:text-primary-600 transition-colors">
                      {test.title}
                    </h3>
                    <span className={`badge badge-${getDifficultyColor(test.difficulty)}`}>
                      {test.difficulty}
                    </span>
                    <span className={`badge badge-${test.isActive ? 'success' : 'secondary'}`}>
                      {test.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  
                  <p className="text-secondary-600 mb-4 leading-relaxed text-sm md:text-base">
                    {test.description}
                  </p>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-4">
                    <div className="flex items-center text-xs md:text-sm text-secondary-600">
                      <Clock className="h-3 w-3 md:h-4 md:w-4 mr-1 md:mr-2" />
                      {test.duration} min
                    </div>
                    <div className="flex items-center text-xs md:text-sm text-secondary-600">
                      <BookOpen className="h-3 w-3 md:h-4 md:w-4 mr-1 md:mr-2" />
                      {test.totalQuestions} Qs
                    </div>
                    <div className="flex items-center text-xs md:text-sm text-secondary-600">
                      <Target className="h-3 w-3 md:h-4 md:w-4 mr-1 md:mr-2" />
                      {test.totalMarks} marks
                    </div>
                    <div className="flex items-center text-xs md:text-sm text-secondary-600">
                      <Users className="h-3 w-3 md:h-4 md:w-4 mr-1 md:mr-2" />
                      {test.totalAttempts || 0} attempts
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div className="flex items-center flex-wrap gap-1">
                      <span className="text-xs text-secondary-500 mr-1">Subjects:</span>
                      {test.subjects.map((subject) => (
                        <span key={subject} className={`badge badge-${getSubjectColor(subject)} badge-sm`}>
                          {subject.charAt(0).toUpperCase() + subject.slice(1)}
                        </span>
                      ))}
                    </div>
                    <div className="text-xs text-secondary-500">
                      Created: {formatDate(test.createdAt)}
                    </div>
                  </div>
                </div>

                <div className="flex md:flex-col items-center md:items-end gap-2 md:gap-3 md:ml-6">
                  <div className="flex items-center flex-wrap gap-1">
                    <button
                      onClick={() => { setSelectedTest(test); setShowTestDetails(true); }}
                      className="p-2 text-secondary-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                      title="View Details"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => window.open(`/admin/tests/${test.id}/questions`, '_blank')}
                      className="p-2 text-secondary-600 hover:text-accent-600 hover:bg-accent-50 rounded-lg transition-colors"
                      title="Manage Questions"
                    >
                      <BookOpen className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => openEditModal(test)}
                      className="p-2 text-secondary-600 hover:text-warning-600 hover:bg-warning-50 rounded-lg transition-colors"
                      title="Edit Test"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => toggleTestStatus(test.id, test.isActive)}
                      className={`p-2 rounded-lg transition-colors ${
                        test.isActive 
                          ? 'text-secondary-600 hover:text-danger-600 hover:bg-danger-50' 
                          : 'text-secondary-600 hover:text-success-600 hover:bg-success-50'
                      }`}
                      title={test.isActive ? 'Deactivate' : 'Activate'}
                    >
                      {test.isActive ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                    </button>
                    <button
                      onClick={() => handleDeleteTest(test.id)}
                      className="p-2 text-secondary-600 hover:text-danger-600 hover:bg-danger-50 rounded-lg transition-colors"
                      title="Delete Test"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="text-right hidden md:block">
                    <div className="text-sm text-secondary-500">Avg Score</div>
                    <div className="text-lg font-semibold text-secondary-900">
                      {test.avgScore?.toFixed(1) || 0}%
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create/Edit Test Modal */}
      {(showCreateModal || showEditModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-secondary-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-secondary-900">
                  {showCreateModal ? 'Create New Test' : 'Edit Test'}
                </h3>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setShowEditModal(false);
                    setSelectedTest(null);
                    resetForm();
                  }}
                  className="text-secondary-400 hover:text-secondary-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            
            <form onSubmit={showCreateModal ? handleCreateTest : handleUpdateTest} className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Basic Information */}
                <div className="md:col-span-2">
                  <h4 className="text-lg font-semibold text-secondary-900 mb-4">Basic Information</h4>
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Test Title *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Enter test title"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Difficulty Level *
                  </label>
                  <select
                    required
                    value={formData.difficulty}
                    onChange={(e) => setFormData({ ...formData, difficulty: e.target.value })}
                    className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Description *
                  </label>
                  <textarea
                    required
                    rows={3}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Enter test description"
                  />
                </div>

                {/* Test Configuration */}
                <div className="md:col-span-2">
                  <h4 className="text-lg font-semibold text-secondary-900 mb-4 mt-6">Test Configuration</h4>
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Duration (minutes) *
                  </label>
                  <input
                    type="number"
                    required
                    min="60"
                    max="300"
                    value={formData.duration}
                    onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Total Questions *
                  </label>
                  <input
                    type="number"
                    required
                    value={formData.totalQuestions}
                    onChange={(e) => setFormData({ ...formData, totalQuestions: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-secondary-50"
                    readOnly
                  />
                  <p className="text-xs text-secondary-500 mt-1">Fixed at 75 for JEE Main format</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Total Marks *
                  </label>
                  <input
                    type="number"
                    required
                    min="100"
                    max="500"
                    value={formData.totalMarks}
                    onChange={(e) => setFormData({ ...formData, totalMarks: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Negative Marks *
                  </label>
                  <input
                    type="number"
                    required
                    min="-2"
                    max="0"
                    value={formData.negativeMarks}
                    onChange={(e) => setFormData({ ...formData, negativeMarks: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                {/* Subjects */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Subjects *
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {formData.subjects.map((subject) => (
                      <span key={subject} className={`badge badge-${getSubjectColor(subject)}`}>
                        {subject.charAt(0).toUpperCase() + subject.slice(1)} (25 questions)
                      </span>
                    ))}
                  </div>
                  <p className="text-xs text-secondary-500 mt-1">Fixed subjects for JEE Main format</p>
                </div>

                {/* Instructions */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Instructions
                  </label>
                  <textarea
                    rows={8}
                    value={formData.instructions}
                    onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
                    className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Enter test instructions for students"
                  />
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex items-center justify-end space-x-3 mt-8 pt-6 border-t border-secondary-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setShowEditModal(false);
                    setSelectedTest(null);
                    resetForm();
                  }}
                  className="px-4 py-2 text-sm font-medium text-secondary-700 bg-white border border-secondary-300 rounded-lg hover:bg-secondary-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={formLoading}
                  className="flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-primary-600 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {formLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                      {showCreateModal ? 'Creating...' : 'Updating...'}
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      {showCreateModal ? 'Create Test' : 'Update Test'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Test Details Modal */}
      {showTestDetails && selectedTest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-secondary-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-secondary-900">Test Details</h3>
                <button
                  onClick={() => setShowTestDetails(false)}
                  className="text-secondary-400 hover:text-secondary-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <div className="space-y-6">
                {/* Basic Info */}
                <div>
                  <h4 className="text-lg font-semibold text-secondary-900 mb-4">{selectedTest.title}</h4>
                  <p className="text-secondary-600 mb-4">{selectedTest.description}</p>
                  
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="p-3 bg-secondary-50 rounded-lg">
                      <div className="text-sm text-secondary-500">Duration</div>
                      <div className="font-semibold text-secondary-900">{selectedTest.duration} minutes</div>
                    </div>
                    <div className="p-3 bg-secondary-50 rounded-lg">
                      <div className="text-sm text-secondary-500">Questions</div>
                      <div className="font-semibold text-secondary-900">{selectedTest.totalQuestions}</div>
                    </div>
                    <div className="p-3 bg-secondary-50 rounded-lg">
                      <div className="text-sm text-secondary-500">Total Marks</div>
                      <div className="font-semibold text-secondary-900">{selectedTest.totalMarks}</div>
                    </div>
                    <div className="p-3 bg-secondary-50 rounded-lg">
                      <div className="text-sm text-secondary-500">Difficulty</div>
                      <span className={`badge badge-${getDifficultyColor(selectedTest.difficulty)}`}>
                        {selectedTest.difficulty}
                      </span>
                    </div>
                    <div className="p-3 bg-secondary-50 rounded-lg">
                      <div className="text-sm text-secondary-500">Status</div>
                      <span className={`badge badge-${selectedTest.isActive ? 'success' : 'secondary'}`}>
                        {selectedTest.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div className="p-3 bg-secondary-50 rounded-lg">
                      <div className="text-sm text-secondary-500">Attempts</div>
                      <div className="font-semibold text-secondary-900">{selectedTest.totalAttempts || 0}</div>
                    </div>
                  </div>
                </div>

                {/* Subjects */}
                <div>
                  <h5 className="font-semibold text-secondary-900 mb-2">Subjects</h5>
                  <div className="flex flex-wrap gap-2">
                    {selectedTest.subjects.map((subject) => (
                      <span key={subject} className={`badge badge-${getSubjectColor(subject)}`}>
                        {subject.charAt(0).toUpperCase() + subject.slice(1)} (25 questions)
                      </span>
                    ))}
                  </div>
                </div>

                {/* Instructions */}
                {selectedTest.instructions && (
                  <div>
                    <h5 className="font-semibold text-secondary-900 mb-2">Instructions</h5>
                    <div className="p-4 bg-secondary-50 rounded-lg">
                      <pre className="text-sm text-secondary-700 whitespace-pre-wrap font-sans">
                        {selectedTest.instructions}
                      </pre>
                    </div>
                  </div>
                )}

                {/* Performance Stats */}
                <div>
                  <h5 className="font-semibold text-secondary-900 mb-4">Performance Statistics</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 border border-secondary-200 rounded-lg">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-primary-600 mb-1">
                          {selectedTest.avgScore?.toFixed(1) || 0}%
                        </div>
                        <div className="text-sm text-secondary-600">Average Score</div>
                      </div>
                    </div>
                    <div className="p-4 border border-secondary-200 rounded-lg">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-success-600 mb-1">
                          {selectedTest.totalAttempts || 0}
                        </div>
                        <div className="text-sm text-secondary-600">Total Attempts</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
